import type {
  CheckoutOptions,
  PaymentTransactionsResponse,
  PricingPlanDetailResponse,
  PricingPlansResponse,
  StripeSubscriptionResponse,
  SubscriptionStatusResponse,
} from '../types'
import { api, ApiError } from './api'

/**
 * Details of a prior payment that covered (or used to cover) a company's
 * period. Present on the coverage response when an earlier payment exists but
 * has expired, so we can tell the user when it lapsed.
 */
export interface PaymentCoveragePaidVia {
  planSlug?: string
  formId?: string
  scope?: string
  paidAt?: string
  /** ISO date the prior coverage expired — used for the "expired on X" message. */
  expiresAt?: string
}

export interface PaymentCoverage {
  /** True when the period is already paid for — no further payment needed. */
  paid: boolean
  /** Set when a prior payment existed but lapsed (drives the expiry message). */
  paidVia?: PaymentCoveragePaidVia | null
  /** ISO date the active coverage expires, when applicable. */
  expiresAt?: string
}

/**
 * Corporation Tax plans cover one filing, for one company, for one year — not
 * unlimited filings. The plan features come from the backend, which still says
 * "Unlimited filings for ONE company", so correct that wording here until the
 * pricing data itself is updated.
 */
const CT_FILING_ALLOWANCE = 'Covers ONE filing for ONE company for ONE year'

const correctPlanFeatures = (
  plansData: PricingPlansResponse
): PricingPlansResponse => {
  const grouped = plansData as unknown as Record<
    string,
    { features?: string[] }[]
  >
  Object.values(grouped).forEach(plans => {
    if (!Array.isArray(plans)) return
    plans.forEach(plan => {
      if (!Array.isArray(plan.features)) return
      plan.features = plan.features.map(feature =>
        /unlimited\s+filings?/i.test(feature) ? CT_FILING_ALLOWANCE : feature
      )
    })
  })
  return plansData
}

/**
 * Payment Service
 *
 * Handles all payment and pricing-related API calls.
 * Integrates with Stripe for secure checkout sessions.
 *
 * @see payment.md for complete API documentation
 */
const paymentService = {
  /**
   * Fetch all active pricing plans
   * Returns plans grouped by productType
   */
  async listPricingPlans(): Promise<PricingPlansResponse> {
    try {
      const result = await api.get<any>('/pricing/plans', {
        skipAuthLogout: true,
      })
      // API envelope: { statusCode, response: { success, data }, message, error }
      return correctPlanFeatures(
        result.response?.data || result.response || result
      )
    } catch (error) {
      console.error('Failed to list pricing plans:', error)
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to load pricing plans', 500)
    }
  },

  /**
   * Get specific pricing plan by slug
   * @param slug - Plan slug (e.g., 'sa-basic')
   */
  async getPricingPlan(slug: string): Promise<PricingPlanDetailResponse> {
    try {
      const response = await api.get<PricingPlanDetailResponse>(
        `/pricing/plans/${slug}`
      )
      return (response as any).data
    } catch (error) {
      console.error(`Failed to get pricing plan: ${slug}`, error)
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to load pricing plan', 500)
    }
  },

  /**
   * Create Stripe checkout session
   * @param slug - Plan slug to purchase
   * @param options - Checkout configuration (success/cancel URLs, email)
   */
  async createCheckout(
    slug: string,
    options: CheckoutOptions
  ): Promise<{ url: string; id: string }> {
    try {
      const result = await api.post<any>(`/payments/checkout/${slug}`, options)
      // API envelope: { statusCode, response: { success, data: { url, id } }, message, error }
      return result.response?.data || result.response || result
    } catch (error) {
      console.error(`Failed to create checkout for plan: ${slug}`, error)
      if (error instanceof ApiError) {
        // Re-throw with original error message for better UX
        throw error
      }
      throw new ApiError('Failed to create checkout session', 500)
    }
  },

  /**
   * Verify payment session (optional)
   * Custom endpoint to verify a checkout session was successful
   * Note: This endpoint may need to be created on the backend
   * @param sessionId - Stripe checkout session ID
   */
  async verifySession(sessionId: string, options?: any): Promise<any> {
    try {
      const response = await api.get(`/payments/verify/${sessionId}`, options)
      return (response as any).data
    } catch (error) {
      console.error(`Failed to verify session: ${sessionId}`, error)
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to verify payment', 500)
    }
  },

  /**
   * Get user's payment transactions
   * Fetches the payment history for the current authenticated user
   * @returns Array of payment transactions with details
   */
  async getTransactions(): Promise<PaymentTransactionsResponse> {
    try {
      const response = await api.get<PaymentTransactionsResponse>(
        '/payments/transactions'
      )
      return (response as any).data
    } catch (error) {
      console.error('Failed to list payment transactions:', error)
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to load payment history', 500)
    }
  },

  /**
   * Get user's subscription status
   * Fetches the current subscription information including active plans
   * @returns Current subscription with status and plan details
   */
  async getSubscription(): Promise<StripeSubscriptionResponse> {
    try {
      const response = await api.get<StripeSubscriptionResponse>(
        '/payments/subscription'
      )
      return (response as any).data
    } catch (error) {
      console.error('Failed to get subscription:', error)
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to load subscription', 500)
    }
  },

  /**
   * Get user's subscription status with enhanced details
   * Recommended approach - provides comprehensive subscription info
   * @returns Detailed subscription status including restrictions and payment info
   */
  async getSubscriptionStatus(): Promise<SubscriptionStatusResponse> {
    try {
      const result = await api.get<any>('/users/subscription-status')
      // API envelope: { statusCode, response: { hasActiveSubscription, subscription, lastPayment, restrictions }, message, error }
      return result.response || result
    } catch (error) {
      console.error('Failed to fetch subscription status:', error)
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to load subscription status', 500)
    }
  },

  /**
   * Check if a specific form has been paid for
   * Endpoint: GET /payments/subscription/:formId
   *
   * Use this before allowing form submission or completion to verify payment.
   *
   * @param formId - The MongoDB ObjectId (_id) from:
   *   - CT600: data._id from POST /filling/ct600 response
   *   - Self Assessment: data._id from SelfAssessment document (requires GET /filling/assessments/my-assessments)
   *
   * @returns Payment status:
   *   - 200: { success: true, data: { status: 'paid', planSlug, ... } } - Payment exists
   *   - 404: Subscription not found - User needs to pay
   *
   * @see formid.md for complete payment integration guide
   */
  async checkFormPayment(
    formId: string,
    options?: any
  ): Promise<{
    success: boolean
    data: { status: string; planSlug?: string }
  }> {
    try {
      const result = await api.get<any>(
        `/payments/subscription/${formId}`,
        options
      )
      // API envelope: { statusCode, response: { success, data: { status, planSlug } }, message, error }
      return result.response || result
    } catch (error) {
      console.error(`Failed to check payment for form ${formId}:`, error)
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to check form payment status', 500)
    }
  },

  /**
   * Check whether the platform fee is already covered for a company's
   * accounting period. The CT600 fee is a one-time payment per company per
   * period — so a user who paid while filing (e.g.) Corporation Tax only is
   * already cleared to come back and file Company Accounts only for the same
   * period without paying again.
   *
   * Endpoint: GET /payments/coverage?companyId=...&periodStart=...&periodEnd=...
   *
   * @returns Coverage status:
   *   - { paid: true }                  — covered, go straight to filing
   *   - { paid: false, paidVia: {...} } — a prior payment exists but expired
   *                                       (paidVia carries the expiry date)
   *   - { paid: false }                 — first-time, payment required
   */
  /**
   * Has this taxpayer already paid for a Self Assessment tax year?
   *
   * The SA counterpart of `getPaymentCoverage`. Coverage is keyed on the tax
   * year, so a user who paid for 2025-26 is not charged again for that year —
   * including on a fresh return if the first draft was discarded.
   */
  async getTaxYearCoverage(taxYear: string, options?: any): Promise<PaymentCoverage> {
    try {
      const result = await api.get<any>(
        `/payments/coverage?taxYear=${encodeURIComponent(taxYear)}`,
        options
      )
      const payload = result.response ?? result
      const data = payload?.data ?? payload
      return {
        paid: Boolean(data?.paid),
        paidVia: data?.paidVia ?? null,
      }
    } catch {
      // Treat a failed lookup as "not covered" — the per-form payment check
      // still gates submission, so this fails safe.
      return { paid: false, paidVia: null }
    }
  },

  async getPaymentCoverage(
    params: { companyId: string; periodStart?: string; periodEnd?: string },
    options?: any
  ): Promise<PaymentCoverage> {
    try {
      const query = new URLSearchParams({ companyId: params.companyId })
      if (params.periodStart) query.set('periodStart', params.periodStart)
      if (params.periodEnd) query.set('periodEnd', params.periodEnd)
      const result = await api.get<any>(
        `/payments/coverage?${query.toString()}`,
        options
      )
      // API envelope: { statusCode, response: { success, data: {...} } | {...} }
      const payload = result.response ?? result
      const data = payload?.data ?? payload
      return {
        paid: Boolean(data?.paid),
        paidVia: data?.paidVia ?? null,
        ...(data?.expiresAt ? { expiresAt: data.expiresAt } : {}),
      }
    } catch (error) {
      console.error('Failed to check payment coverage:', error)
      throw error instanceof ApiError
        ? error
        : new ApiError('Failed to check payment coverage', 500)
    }
  },
}

export default paymentService
